import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Col, Form, Row, Button, Image, Tabs, Tab } from "react-bootstrap";
import {
  getAllTypes,
  getProduct,
  updateProduct,
  getAllPartiners,
  getAllRibbonBadges,
} from "../../services/api";
import LoadingIndicator from "../LoadingIndicator";
import SuccessAlert from "../SuccessAlert";
import ErrorAlert from "../ErrorAlert";
import { UpdateProductSchema } from "../../utils/validation_schemas";
import { formatDate } from "../../utils/formatters";
import useNotification from "../../contexts/notification";
import { UpdateProductData } from "../../utils/payloads";
import useCategories from "../../hooks/useCategories";
import { currency, unformatPrice } from "../../utils/mappers";
import { NumericFormat } from "react-number-format";
import Select from "react-select";
import ProductImageGalleryModal from "../ProductImageGalleryModal";
export default function UpdateProductForm({ productId, onSubmit }) {
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState("start");
  const categories = useCategories();
  const groupedCategoryOptions = useMemo(() => {
    return categories.map((category) => ({
      label: category.name,
      options: category.category_types.map((sub) => ({
        label: sub.name,
        value: sub.id,
      })),
    }));
  }, [categories]);


  const [promotionCategory, setPromotionCategory] = useState([]);
  const clearStatus = () => setStatus({ status: "start" });
  const [submitting, setSubmitting] = useState(false);
  const { setMessage } = useNotification();
  const [partners, setPartners] = useState([]);
  const [badge, setBadge] = useState([]);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [isApplyingGalleryImage, setIsApplyingGalleryImage] = useState(false);
  const [imageTabKey, setImageTabKey] = useState("upload");

  const handleOpenGallery = () => {
    setShowGalleryModal(true);
  };

  const handleCloseGallery = () => {
    if (!isApplyingGalleryImage) {
      setShowGalleryModal(false);
    }
  };

  const handleImageUploadChange = (event) => {
    const file = event.currentTarget.files?.[0];
    setSelectedGalleryImage(null);
    setImageTabKey("upload");
    formik.setFieldValue("image", file);
  };

  const handleSelectGalleryImage = async (image) => {
    setIsApplyingGalleryImage(true);

    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const fileName = image.file_name || `imagem-${image.id}.jpg`;
      const file = new File([blob], fileName, {
        type: blob.type || "image/jpeg",
      });

      formik.setFieldValue("image", file);
      formik.setFieldTouched("image", true, false);
      setSelectedGalleryImage(image);
      setImageTabKey("gallery");
      setShowGalleryModal(false);
    } catch (error) {
      console.error("Erro ao selecionar imagem da galeria", error);
      setMessage({
        error: true,
        content: "Não foi possível carregar a imagem selecionada.",
      });
    } finally {
      setIsApplyingGalleryImage(false);
    }
  };

  useEffect(() => {
    getAllPartiners().then((data) => {

      setPartners(data);
    });
  }, []);

  useEffect(() => {

    getAllRibbonBadges().then((data) => {

      setBadge(data);
    });
  }, []);

  useEffect(() => {
    getProduct(productId).then((data) => {
      const { product } = data;

      formik.setValues({
        id: product.id,
        name: product.name,
        description: product.description,
        image: undefined,
        previous_price: product.previous_price,
        offer_price: product.offer_price,

        link: product.link,
        partner_id: product.partner_id,
        send_notifications: "0",
        title: "",
        ribbon_badges: product.ribbon_badges.length > 0 ? product.ribbon_badges[0].id.toString() : "0",
        created_at: product.created_at
          ? new Date(product.created_at).toLocaleString("sv-SE", { timeZone: "America/Sao_Paulo" }).replace(" ", "T").slice(0, 16)
          : "",


        have_cupom: String(product.have_cupom),
        price_on_offer: product.price_on_offer ? "1" : "0",
        wholesale_price: product.wholesale_price || "",
        min_quantity_wholesale: product.min_quantity_wholesale || "",
        category_type_id: product.category_type_id,
        cupom: product.cupom || "",
        cupom_validity: product.cupom_validity || "",

      });
      setSelectedGalleryImage(null);
      setImageTabKey("upload");
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      image: undefined,
      previous_price: "",
      title: "",
      link: "",
      send_notifications: "0",
      partner_id: undefined,
      have_cupom: "0",
      category_type_id: undefined,
      cupom: "",
      cupom_validity: "",
      price_on_offer: "0",
      min_quantity_wholesale: "",
      offer_price: "",
      ribbon_badges: "0",
      created_at: ""
    },
    validationSchema: UpdateProductSchema,
    onSubmit: async (values) => {
      if (values.image === undefined) {
        delete values.image;
      }

      setSubmitting(true);

      try {
        console.log("Valores a serem enviados:", values);

        const payload = UpdateProductData(values);
        const updated = await updateProduct(payload);

        if (updated) {
          setMessage({
            error: false,
            content: "Produto atualizado com sucesso",
          });

          setStatus("success");

          // 🔒 Só fecha o modal depois de tudo
          setTimeout(() => {
            onSubmit?.(); // ← Isso aqui provavelmente fecha o modal
          }, 300); // pequeno delay opcional
        } else {
          setMessage({
            error: true,
            content: "Erro ao atualizar produto",
          });
          setStatus("error");
        }

      } catch (error) {
        console.error("Erro ao atualizar:", error);
        setMessage({
          error: true,
          content: "Ocorreu um erro inesperado.",
        });
        setStatus("error");
      } finally {
        setSubmitting(false);
      }
    }

  });

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <Form noValidate onSubmit={formik.handleSubmit} className="mt-3">
      <h6 className="mb-3 text-primary">Dados do produto</h6>
      <Form.Group className="mt-1 mb-4" controlId="formProductData">
        <Row>
          <Col xl={8} md={8}>
            <Form.Label>Nome do produto</Form.Label>
            <Form.Control
              className="my-1"
              type="text"
              name="name"
              placeholder="Nome do produto"
              required
              onChange={formik.handleChange}
              value={formik.values.name}
              isValid={formik.touched.name && !formik.errors.name}
              isInvalid={formik.errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.name}
            </Form.Control.Feedback>
          </Col>

          <Col xl={4} md={4} sm={5}>
            <Form.Label>Preço Anterior *</Form.Label>
            <NumericFormat
              name="previous_price"
              value={formik.values.previous_price}
              thousandSeparator="."
              decimalSeparator=","
              allowNegative={false}
              isNumericString
              onValueChange={({ floatValue }) => {
                formik.setFieldValue("previous_price", floatValue ?? "");
              }}
              customInput={Form.Control}
              className="my-1"
              placeholder="Preço Anterior"
              isInvalid={!!formik.errors.previous_price}
              isValid={
                formik.touched.previous_price && !formik.errors.previous_price
              }
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.price}
            </Form.Control.Feedback>
          </Col>
          <Col xl={6} md={6} sm={6}>
            <Form.Label>Preço da Oferta *</Form.Label>
            <NumericFormat
              name="offer_price"
              value={formik.values.offer_price}
              thousandSeparator="."
              decimalSeparator=","
              allowNegative={false}
              isNumericString
              onValueChange={({ floatValue }) => {
                formik.setFieldValue("offer_price", floatValue ?? "");
              }}
              customInput={Form.Control}
              className="my-1"
              placeholder="Preço de Oferta"
              isInvalid={!!formik.errors.offer_price}
              isValid={
                formik.touched.offer_price && !formik.errors.offer_price
              }
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.offer_price}
            </Form.Control.Feedback>
            <Form.Check
              className="my-1"
              type="checkbox"
              name="price_on_offer"
              label="Preço em Oferta"
              onChange={(e) => formik.setFieldValue("price_on_offer", e.target.checked ? "1" : "0")}
              checked={formik.values.price_on_offer === "1"}
            />
          </Col>
          <Form.Group>
            <h6 className="mb-3 text-primary center">Emblemas no Produto</h6>
            {console.log(formik.values.ribbon_badges[0])}
            <fieldset>
              <h6 className="text-danger">{formik.errors.ribbon_badges}</h6>
              <Row>
                {badge && badge.map((f) => {
                  return (
                    <Col sm={12} md={6} lx={6} key={f.id}>
                      <Form.Check
                        custom
                        id={`custom-checkbox-${f.id}`}
                        name="ribbon_badges"
                        type="radio"
                        isInvalid={formik.touched.ribbon_badges && !!formik.errors.ribbon_badges}
                        value={f.id}
                        label={f.name}
                        checked={formik.values.ribbon_badges === String(f.id)}


                        onChange={(e) => formik.setFieldValue("ribbon_badges", e.target.value)}




                      />
                    </Col>
                  );
                })}

              </Row>
            </fieldset>
          </Form.Group>
          <Col xl={12} md={12} sm={12}>
            <Form.Label>Descrição *</Form.Label>
            <Form.Control
              className="my-1"
              as="textarea"
              rows={3}
              name="description"
              placeholder="Descrição do produto"
              onChange={formik.handleChange}
              value={formik.values.description}
              isValid={formik.touched.description && !formik.errors.description}
              isInvalid={formik.errors.description}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.description}
            </Form.Control.Feedback>
          </Col>
          <Form.Group className="mt-3">
            <Tabs
              activeKey={imageTabKey}
              onSelect={(key) => setImageTabKey(key || "upload")}
              className="product-image-tabs"
            >
              <Tab eventKey="upload" title="Fazer upload">
                <div className="pt-3">
                  <Form.File
                    accept="image/avif,image/webp,image/png,image/jpeg"
                    name="image"
                    lang="pt"
                    label={
                      formik.values.image
                        ? formik.values.image.name
                        : "Escolher arquivo"
                    }
                    onChange={handleImageUploadChange}
                    isInvalid={formik.errors.image}
                    feedback={formik.errors.image}
                    custom
                  />
                </div>
              </Tab>
              <Tab eventKey="gallery" title="Galeria de imagens">
                <div className="pt-3">
                  <p className="text-muted mb-3">
                    Busque imagens já utilizadas para reutilizar no produto.
                  </p>
                  <Button
                    variant="outline-primary"
                    type="button"
                    onClick={handleOpenGallery}
                    disabled={isApplyingGalleryImage}
                  >
                    Buscar imagem
                  </Button>
                  {isApplyingGalleryImage && (
                    <span className="text-muted small ms-3">
                      Carregando imagem selecionada…
                    </span>
                  )}
                </div>
              </Tab>
            </Tabs>
            {selectedGalleryImage && (
              <div className="mt-4">
                <Form.Label className="d-block">Imagem selecionada</Form.Label>
                <div
                  className="p-3 bg-light border rounded d-inline-flex flex-column align-items-center"
                  style={{ minWidth: "200px" }}
                >
                  <Image
                    src={selectedGalleryImage.thumbnail_url || selectedGalleryImage.url}
                    alt={selectedGalleryImage.product_name || selectedGalleryImage.file_name}
                    rounded
                    style={{ width: "160px", height: "120px", objectFit: "cover" }}
                  />
                  <small className="text-muted mt-2 text-center">
                    {selectedGalleryImage.product_name || selectedGalleryImage.file_name}
                  </small>
                </div>
              </div>
            )}
          </Form.Group>
          <Col xl={12} md={12} sm={12}>
            <Form.Label>Link de Oferta</Form.Label>
            <Form.Control
              className="my-1"
              type="url"
              name="link"
              placeholder="http://exemplo-de-oferta.com"
              onChange={formik.handleChange}
              value={formik.values.link}
              isValid={formik.touched.link && !formik.errors.link}
              isInvalid={formik.errors.link}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.link}
            </Form.Control.Feedback>
          </Col>

          <Col xl={6} md={6} sm={6}>
            <Form.Label>Parceiros *</Form.Label>
            <Select
              id="partner_id"
              name="partner_id"
              options={partners.map(partner => ({
                value: partner.id,
                label: partner.name
              }))}
              value={partners
                .map(partner => ({ value: partner.id, label: partner.name }))
                .find(option => option.value === formik.values.partner_id)}
              onChange={option => formik.setFieldValue('partner_id', option.value)}
              onBlur={() => formik.setFieldTouched('partner_id', true)}
              placeholder="Selecione um parceiro"
              noOptionsMessage={() => "Nenhum parceiro encontrado"}
              classNamePrefix="select"
            />
            {formik.touched.partner_id && formik.errors.partner_id && (
              <div className="invalid-feedback d-block">
                {formik.errors.partner_id}
              </div>
            )}
          </Col>
          <Form.Group as={Col} xl={6} md={6} sm={6}>
            <Form.Label>Subcategoria *</Form.Label>
            <Select
              options={groupedCategoryOptions}
              name="category_type_id"
              value={
                groupedCategoryOptions
                  .flatMap(group => group.options)
                  .find(option => option.value === formik.values.category_type_id)
              }
              onChange={(option) =>
                formik.setFieldValue("category_type_id", option?.value ?? "")
              }
              onBlur={() => formik.setFieldTouched("category_type_id", true)}
              placeholder="Selecione uma subcategoria"
              noOptionsMessage={() => "Nenhuma subcategoria encontrada"}
              classNamePrefix="select"
            />
            {formik.touched.category_type_id && formik.errors.category_type_id && (
              <div className="invalid-feedback d-block">
                {formik.errors.category_type_id}
              </div>
            )}
          </Form.Group>







          <Col xl={6} md={6} sm={6}>
            <Form.Label>Cupom *</Form.Label>
            <Form.Control
              className="my-1"
              type="text"
              name="cupom"
              placeholder="Cupom Disponivel"
              onChange={formik.handleChange}
              value={formik.values.cupom}
              isValid={formik.touched.cupom && !formik.errors.cupom}
              isInvalid={formik.errors.cupom}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.cupom}
            </Form.Control.Feedback>
          </Col>
          <Col xl={6} md={6} sm={6}>
            <Form.Label>Data de expiração do Cupom *</Form.Label>
            <Form.Control
              className="my-1"
              type="date"
              name="cupom_validity"
              min={formatDate(new Date())}
              placeholder="Data de expiração do Cupom"
              onChange={formik.handleChange}
              value={formik.values.cupom_validity}
              isValid={
                formik.touched.cupom_validity &&
                !formik.errors.cupom_validity
              }
              isInvalid={formik.errors.cupom_validity}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.cupom_validity}
            </Form.Control.Feedback>
          </Col>
          <Col xl={6} md={6} sm={6}>
            <Form.Label>Data e Hora da Publicação</Form.Label>
            <Form.Control
              className="my-1"
              type="datetime-local"
              name="created_at"
              placeholder="Data da publicação"
              onChange={formik.handleChange}
              value={formik.values.created_at}
              isValid={formik.touched.created_at && !formik.errors.created_at}
              isInvalid={formik.errors.created_at}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.created_at}
            </Form.Control.Feedback>
          </Col>

        </Row>
        {status === "success" && (
          <SuccessAlert msg="Produto atualizado" onClose={clearStatus} />
        )}

        {status === "error" && (
          <ErrorAlert msg="Um erro ocorreu." onClose={clearStatus} />
        )}
        <Col>
          <Form.Check
            className="my-1"
            type="checkbox"
            name="send_notifications"
            label="Enviar Notificações?"
            onChange={(e) => {
              formik.setFieldValue("send_notifications", e.target.checked ? "1" : "0");

            }}
            value={formik.values.send_notifications === "1"}
          />
          {formik.values.send_notifications == "1" && (

            <Form.Group controlId="formTitle">

              <Form.Control
                type="text"
                name="title"
                placeholder="Digite o título"
                onChange={formik.handleChange}
                value={formik.values.title}
              />
            </Form.Group>

          )}
          <Row>

            <Button
              className="my-2"
              variant="primary"
              type="submit"
              disabled={submitting}
            >
              Atualizar
            </Button>
          </Row>

        </Col>
      </Form.Group>
      </Form>

      <ProductImageGalleryModal
        show={showGalleryModal}
        onHide={handleCloseGallery}
        onSelect={handleSelectGalleryImage}
        selectedImageId={selectedGalleryImage?.id}
        isConfirmingSelection={isApplyingGalleryImage}
      />
    </>
  );
}
